import { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';

// 환경변수 기반 (api.js와 동일 정책)
const API = process.env.REACT_APP_API_URL || '';

/**
 * AuthGate - 로그인/회원가입 풀스크린 게이트
 * 좌측: 서비스 소개 + 투자 경고 (모바일에서는 자동 숨김)
 * 우측: 로그인/회원가입 폼 + 안내(펼침)
 */
export default function AuthGate({ children }) {
  const { isAuthenticated, login } = useAuth();
  const [tab, setTab] = useState('login');
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 600
  );

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < 600);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (isAuthenticated) {
    return children;
  }

  // 모바일에서는 단일 컬럼 + 좌측 패널 숨김
  const cardStyle = isMobile
    ? { ...styles.card, gridTemplateColumns: '1fr', maxWidth: 420, minHeight: 'auto' }
    : styles.card;

  return (
    <div style={styles.wrap}>
      <div style={cardStyle}>
        {/* 좌측 패널 - 모바일에서는 숨김 */}
        {!isMobile && (
          <div style={styles.leftPanel}>
            {/* 상단 - 브랜드 */}
            <div>
              <div style={styles.brand}>
                Stock<span style={{ color: '#f0883e' }}>Tool</span>
              </div>
              <div style={styles.brandSub}>한국 주식 도구 · stocktool.co.kr</div>
            </div>

            {/* 중앙 - 헤드라인 (가운데 정렬) */}
            <div style={styles.headlineWrap}>
              <div style={styles.headline}>
                놓치지 않는<br/>
                <span style={{ color: '#f0883e' }}>시장의 모든 순간</span>
              </div>
              <div style={styles.headlineSub}>
                남들이 못 보는 종목까지 한 번에.<br/>
                메이저 언론부터 작은 매체 단독 보도까지.
              </div>
            </div>

            {/* 하단 - 스탯 */}
            <div style={styles.statsRow}>
              <div style={styles.statItem}>
                <div style={styles.statNum}>150+α</div>
                <div style={styles.statLabel}>stocks tracked</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statNum}>1·5분</div>
                <div style={styles.statLabel}>auto refresh</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statNum}>3 mode</div>
                <div style={styles.statLabel}>protected</div>
              </div>
            </div>
          </div>
        )}

        {/* 우측 패널 - 로그인/회원가입 */}
        <div style={styles.rightPanel}>
          {/* 상단 그룹: 모바일 브랜드/경고 + 탭 + 폼 + 안내 */}
          <div style={styles.rightContent}>
            {/* 모바일에서만 - 브랜드 + 투자 경고 */}
            {isMobile && (
              <>
                <div style={styles.mobileBrand}>
                  Stock<span style={{ color: '#f0883e' }}>Tool</span>
                  <span style={styles.mobileBrandSub}>주식도구</span>
                </div>
                <div style={styles.warningBox}>
                  <div style={styles.warningTitle}>⚠ 투자 위험 고지</div>
                  <div style={styles.warningBody}>
                    본 서비스는 <strong style={{ color: '#f85149' }}>시장 정보 제공 도구</strong>로,
                    투자 권유나 자문이 아니며,<br/>모든 투자 판단과 손실 책임은
                    <strong style={{ color: '#f85149' }}> 전적으로 본인에게</strong> 있습니다.
                  </div>
                </div>
              </>
            )}

            {/* 서비스 소개 링크 (proFast로 이동) */}
            <div style={styles.introLinkWrap}>
              <a href="/proFast" style={styles.introLink}>📖 서비스 소개 보기 →</a>
            </div>

            {/* 탭 */}
            <div style={styles.tabs}>
              {[
                { key: 'login', label: '로그인' },
                { key: 'signup', label: '회원가입' },
              ].map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  ...styles.tabBtn,
                  background: tab === t.key ? '#f0883e' : 'transparent',
                  color: tab === t.key ? '#000' : '#8b949e',
                  fontWeight: tab === t.key ? 700 : 400,
                }}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'login'
              ? <LoginForm onLogin={login} />
              : <SignupForm onDone={() => setTab('login')} />}

            {/* 안내 - 펼침 형식 */}
            <details style={styles.details}>
              <summary style={styles.summary}>🔒 비밀번호 분실 안내</summary>
              <div style={styles.detailsBody}>
                비밀번호는 <strong>암호화 단방향 저장</strong>되어 운영자도 확인할 수 없습니다.
                분실 시 카카오톡 문의로 <strong>임시 비밀번호 재발급</strong>만 가능합니다.<br/>
                로그인 후 안전한 곳에 보관해 주세요.
              </div>
            </details>

            <details style={styles.details}>
              <summary style={styles.summary}>💳 구독 / 환불 안내</summary>
              <div style={styles.detailsBody}>
                월 33,000원 (부가세 포함)<br/>
                🎉 개설 이벤트: 7일 추가 무료 (총 37일)<br/>
                🎁 주말 결제 시 추가 (토 +3일 / 일 +2일)<br/>
                결제: 계좌이체 · 7일 이내 전액 환불<br/>
                문의: <a href="https://open.kakao.com/o/gdjFE7wi" target="_blank" rel="noopener" style={{ color: '#FEE500' }}>카카오톡 오픈채팅</a>
                {' · '}
                <a href="mailto:stocktool@naver.com" style={{ color: '#f0883e' }}>stocktool@naver.com</a>
              </div>
            </details>
          </div>

          {/* 우측 최하단 - 투자 경고 (PC만, 모바일은 상단에 이미 있음) */}
          {!isMobile && (
            <div style={styles.warningBoxBottom}>
              <div style={styles.warningTitleBottom}>⚠ 투자 위험 고지</div>
              <div style={styles.warningBodyBottom}>
                본 서비스는 <strong style={{ color: '#f85149' }}>시장 정보 제공 도구</strong>로,
                투자 권유나 자문이 아니며,<br/>모든 투자 판단과 손실 책임은
                <strong style={{ color: '#f85149' }}> 전적으로 본인에게</strong> 있습니다.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function LoginForm({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || '로그인 실패');
        return;
      }

      if (!data.subscription_active) {
        setError('구독이 활성화되지 않았습니다.\n입금 확인 후 승인되면 이용 가능합니다.');
        return;
      }

      onLogin(data.token, data.user);
    } catch (err) {
      setError('서버 연결 실패. 잠시 후 다시 시도하세요.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <input type="email" placeholder="이메일" value={email}
        onChange={(e) => setEmail(e.target.value)} required autoFocus
        style={styles.input} />
      <input type="password" placeholder="비밀번호" value={password}
        onChange={(e) => setPassword(e.target.value)} required
        style={styles.input} />
      {error && <div style={styles.error}>{error}</div>}
      <button type="submit" disabled={loading} style={{
        ...styles.btn, opacity: loading ? 0.6 : 1,
        cursor: loading ? 'wait' : 'pointer',
      }}>
        {loading ? '로그인 중...' : '로그인'}
      </button>
    </form>
  );
}


function SignupForm({ onDone }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [phone, setPhone]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, phone }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || '가입 실패');
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError('서버 연결 실패. 잠시 후 다시 시도하세요.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div style={styles.successBox}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#3fb950' }}>
          가입 완료
        </div>
        <div style={{ fontSize: 13, color: '#c9d1d9', lineHeight: 1.7, marginBottom: 16 }}>
          가입 신청이 접수되었습니다.<br/>
          아래 카카오톡 오픈채팅으로<br/>
          <strong style={{ color: '#f0883e' }}>이름 + 이메일</strong>을 보내주시면<br/>
          계좌 안내 후 입금 확인 시 즉시 활성화됩니다.
        </div>
        <a href="https://open.kakao.com/o/gdjFE7wi" target="_blank" rel="noopener"
          style={{
            display: 'block', textAlign: 'center', padding: '11px',
            background: '#FEE500', color: '#3C1E1E', fontWeight: 700,
            borderRadius: 6, textDecoration: 'none', fontSize: 13, marginBottom: 10,
          }}>
          💬 카카오톡 오픈채팅 입장
        </a>
        <button onClick={onDone} style={{ ...styles.btn, width: '100%' }}>
          로그인 화면으로
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <input type="email" placeholder="이메일 (로그인 ID)" value={email}
        onChange={(e) => setEmail(e.target.value)} required autoFocus
        style={styles.input} />
      <input type="password" placeholder="비밀번호 (8자 이상)" value={password}
        onChange={(e) => setPassword(e.target.value)} required minLength={8}
        style={styles.input} />
      <input type="text" placeholder="이름 (입금자명과 동일)" value={name}
        onChange={(e) => setName(e.target.value)} required
        style={styles.input} />
      <input type="tel" placeholder="휴대번호 (010-0000-0000)" value={phone}
        onChange={(e) => setPhone(e.target.value)} required
        style={styles.input} />
      <div style={styles.hint}>
        ⓘ 입금자명과 이름이 같아야 자동 매칭됩니다
      </div>
      {error && <div style={styles.error}>{error}</div>}
      <button type="submit" disabled={loading} style={{
        ...styles.btn, opacity: loading ? 0.6 : 1,
        cursor: loading ? 'wait' : 'pointer',
      }}>
        {loading ? '가입 중...' : '가입 신청'}
      </button>
    </form>
  );
}


const styles = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#080c10',
    padding: 20,
    backgroundImage:
      'linear-gradient(rgba(240,136,62,0.03) 1px, transparent 1px),' +
      'linear-gradient(90deg, rgba(240,136,62,0.03) 1px, transparent 1px)',
    backgroundSize: '40px 40px',
  },
  card: {
    width: '100%',
    maxWidth: 760,
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    minHeight: 540,
  },
  // 좌측 - 미니멀 타이포 + 그리드
  leftPanel: {
    padding: '40px 32px',
    borderRight: '1px solid #21262d',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundImage:
      'linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px),' +
      'linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
  },
  brand: {
    fontSize: 30, fontWeight: 600, color: '#e6edf3',
    letterSpacing: '-1.2px', marginBottom: 4,
  },
  brandSub: {
    fontSize: 10, color: '#6e7681',
    letterSpacing: '3px', textTransform: 'uppercase',
    marginBottom: 28,
  },
  warningBox: {
    background: 'rgba(248,81,73,0.06)',
    border: '1px solid rgba(248,81,73,0.25)',
    borderLeft: '3px solid #f85149',
    borderRadius: 4,
    padding: '12px 14px',
    marginBottom: 28,
  },
  warningTitle: {
    fontSize: 11, fontWeight: 700, color: '#f85149',
    letterSpacing: 1, marginBottom: 6,
  },
  warningBody: {
    fontSize: 11, color: '#c9d1d9', lineHeight: 1.8,
  },
  headline: {
    fontSize: 26, fontWeight: 500, color: '#c9d1d9',
    lineHeight: 1.35, letterSpacing: '-0.5px',
    marginBottom: 14,
  },
  headlineSub: {
    fontSize: 12, color: '#8b949e', lineHeight: 1.7,
  },
  statsRow: {
    display: 'flex', gap: 18,
    paddingTop: 22, marginTop: 24,
    borderTop: '1px solid #21262d',
  },
  statItem: {},
  statNum: {
    fontSize: 18, fontWeight: 600, color: '#f0883e',
    letterSpacing: '-0.5px', marginBottom: 2,
  },
  statLabel: {
    fontSize: 9, color: '#6e7681',
    letterSpacing: 1, textTransform: 'uppercase',
  },

  // 우측 - 로그인 폼
  // 중앙 헤드라인 wrapper (가운데 정렬)
  headlineWrap: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexGrow: 1,
    paddingTop: 8,
    paddingBottom: 8,
  },
  rightPanel: {
    padding: '40px 32px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  // 우측 폼 영역 그룹 (탭 + 폼 + 안내)
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  // 서비스 소개 링크 (proFast로 이동)
  introLinkWrap: {
    textAlign: 'right',
    marginBottom: 10,
  },
  introLink: {
    fontSize: 12,
    color: '#f0883e',
    textDecoration: 'none',
    padding: '4px 10px',
    border: '1px solid rgba(240,136,62,0.3)',
    borderRadius: 4,
    background: 'rgba(240,136,62,0.05)',
    display: 'inline-block',
  },
  tabs: {
    display: 'flex',
    border: '1px solid #30363d',
    borderRadius: 4,
    padding: 3,
    marginBottom: 18,
    gap: 3,
  },
  tabBtn: {
    flex: 1,
    fontSize: 13,
    padding: '8px 0',
    border: 'none',
    borderRadius: 3,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  input: {
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: 4,
    padding: '11px 14px',
    color: '#e6edf3',
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
  },
  hint: { fontSize: 11, color: '#8b949e', padding: '4px 2px' },
  error: {
    background: 'rgba(248,81,73,0.1)',
    border: '1px solid rgba(248,81,73,0.3)',
    borderRadius: 4,
    padding: '8px 12px',
    color: '#f85149',
    fontSize: 12,
    lineHeight: 1.5,
    whiteSpace: 'pre-line',
  },
  btn: {
    background: '#f0883e',
    color: '#000',
    fontWeight: 700,
    border: 'none',
    borderRadius: 4,
    padding: '12px',
    fontSize: 14,
    marginTop: 4,
  },
  successBox: {
    textAlign: 'center',
    padding: '8px 4px',
  },
  // 펼침 안내
  details: {
    marginTop: 10,
    fontSize: 12,
  },
  summary: {
    color: '#8b949e',
    cursor: 'pointer',
    padding: '6px 0',
    userSelect: 'none',
    listStyle: 'none',
  },
  detailsBody: {
    background: '#161b22',
    border: '1px solid #21262d',
    borderRadius: 4,
    padding: '10px 12px',
    marginTop: 4,
    color: '#8b949e',
    fontSize: 11,
    lineHeight: 1.7,
  },
  // 우측 패널 최하단 투자 경고
  warningBoxBottom: {
    background: 'rgba(248,81,73,0.06)',
    border: '1px solid rgba(248,81,73,0.25)',
    borderLeft: '3px solid #f85149',
    borderRadius: 4,
    padding: '12px 14px',
    marginTop: 16,
  },
  warningTitleBottom: {
    fontSize: 11, fontWeight: 700, color: '#f85149',
    letterSpacing: 1, marginBottom: 6,
  },
  warningBodyBottom: {
    fontSize: 10.5, color: '#c9d1d9', lineHeight: 1.8,
    wordBreak: 'keep-all',
    whiteSpace: 'normal',
  },
  // 모바일 화면에서만 보이는 브랜드 + 경고
  mobileBrand: {
    fontSize: 22, fontWeight: 600, color: '#e6edf3',
    letterSpacing: '-0.8px', textAlign: 'center',
    marginBottom: 14,
  },
  mobileBrandSub: {
    fontSize: 11, color: '#8b949e',
    marginLeft: 6, fontWeight: 400,
  },
};
